import React, { useEffect, useLayoutEffect, useState } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useInteractions,
  useDismiss,
} from '@floating-ui/react'
import { CommentPanel } from './comment-panel'
import type { Comment } from './types'

export interface FloatingCommentWrapperProps {
  comment?: Comment | null
  comments?: Comment[]
  blockId?: string
  rangeText?: string
  isAddingComment?: boolean
  isRangeComment?: boolean
  referenceElement: HTMLElement | null
  onAddComment: (
    content: string,
    extra?: { mentions?: string[]; richContent?: any }
  ) => void
  onReply?: (parentId: string, content: string) => void
  onResolve?: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onEditComment?: (commentId: string, content: string) => void
  onDeleteComment?: (commentId: string) => void
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  onClose: () => void
  // 控制仅展示内容（隐藏回复与操作），用于 block 右侧按钮弹窗
  showContent?: boolean
  // 新增：在浮窗中点击某个线程时通知外部（右侧面板跳转高亮）
  onThreadActivate?: (commentId: string) => void
}

export function FloatingCommentWrapper(props: FloatingCommentWrapperProps) {
  const {
    comment,
    comments = [],
    blockId,
    rangeText,
    isAddingComment = false,
    isRangeComment = false,
    referenceElement,
    onAddComment,
    onReply,
    onResolve,
    onEditComment,
    onDeleteComment,
    onApplySuggestion,
    onDismissCheck,
    onClose,
    showContent,
    onThreadActivate,
  } = props

  const [open, setOpen] = React.useState(true)

  // For add-comment popover, avoid flicker by using fixed positioning and disabling autoUpdate
  const staticPosition = Boolean(isAddingComment)
  const [isPositioned, setIsPositioned] = useState(false)
  const hasPositionedOnceRef = React.useRef(false)
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({
        padding: 10,
        fallbackPlacements: ['top-start', 'right', 'left'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      hasPositionedOnceRef.current = false
      setIsPositioned(false)
      return autoUpdate(
        reference,
        floating,
        () => {
          update()
          if (!hasPositionedOnceRef.current) {
            hasPositionedOnceRef.current = true
            setIsPositioned(true)
          }
        },
        {
          elementResize: true,
          layoutShift: true,
          ancestorResize: true,
          ancestorScroll: !staticPosition,
          animationFrame: false,
        }
      )
    },
  })

  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    ancestorScroll: true,
  })

  // Handle dismiss event
  useEffect(() => {
    if (!open) {
      onClose()
    }
  }, [open, onClose])

  const { getFloatingProps } = useInteractions([dismiss])

  useEffect(() => {
    if (referenceElement) {
      refs.setReference(referenceElement)
    }
  }, [referenceElement, refs])

  // Delay first paint until reference is attached to avoid initial jump
  const [ready, setReady] = useState(staticPosition ? false : true)
  useLayoutEffect(() => {
    if (!staticPosition) return
    if (referenceElement) {
      const raf = window.requestAnimationFrame(() => setReady(true))
      return () => window.cancelAnimationFrame(raf)
    }
  }, [referenceElement, staticPosition])

  // Determine which comments to show based on context
  const relevantComments = React.useMemo(() => {
    if (comment?.id) {
      // If we have a specific comment, show its thread
      const thread = [comment]
      const replies = comments.filter((c) => c.parentId === comment.id)
      return [...thread, ...replies]
    }

    // For range comments, the comments are already filtered and passed in
    if (rangeText && comments.length > 0) {
      return comments
    }

    if (blockId) {
      // Show all comments for this block
      const filtered = comments.filter(
        (c) => c.docBlockId === blockId || c.metadata?.blockId === blockId
      )
      return filtered
    }
    return comments
  }, [comment, comments, blockId, rangeText])

  // Comments will be shown by CommentPanel based on relevantComments

  if (!open || !referenceElement || (staticPosition && !ready)) {
    return null
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          zIndex: 49,
          visibility: isPositioned ? 'visible' : 'hidden',
          willChange: 'transform',
        }}
        {...getFloatingProps()}
      >
        <CommentPanel
          blockId={blockId || ''}
          comments={relevantComments}
          onAddComment={(content, extra) => {
            setOpen(false)
            onAddComment(content, extra)
          }}
          onReply={(parentId, content) => {
            console.log('[FloatingCommentWrapper] onReply invoked', {
              parentId,
              content,
              hasHandler: typeof onReply === 'function',
            })
            onReply?.(parentId, content)
          }}
          onResolve={(id, res) => {
            console.log('[FloatingCommentWrapper] onResolve', { id, res })
            onResolve?.(id, res)
          }}
          onClose={() => {
            console.log('[FloatingCommentWrapper] onClose')
            onClose()
          }}
          rangeText={rangeText}
          onApplySuggestion={(id) => {
            console.log('[FloatingCommentWrapper] onApplySuggestion', { id })
            onApplySuggestion?.(id)
          }}
          onDismissCheck={(id) => {
            console.log('[FloatingCommentWrapper] onDismissCheck', { id })
            onDismissCheck?.(id)
          }}
          onEditComment={(id, content) => {
            console.log('[FloatingCommentWrapper] onEditComment', {
              id,
              content,
            })
            onEditComment?.(id, content)
          }}
          onDeleteComment={(id) => {
            console.log('[FloatingCommentWrapper] onDeleteComment', { id })
            onDeleteComment?.(id)
          }}
          isRangeComment={isRangeComment}
          isAddingComment={isAddingComment}
          showContent={showContent}
          onThreadActivate={(cid) => {
            console.log('[FloatingCommentWrapper] onThreadActivate', {
              commentId: cid,
            })
            onThreadActivate?.(cid)
          }}
        />
      </div>
    </FloatingPortal>
  )
}
